<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { useWatch } from '@/core/utils/composables/useWatch';
import Selector2 from '@/components/formsExt/selector2/selector2.vue';
import type { Item as SelectorItem } from '@/components/formsExt/selector2/types';
import { apiSetSearchParamsFilter, ITEM_ID_ALL, ITEM_ID_NEW } from '@/components/formsExt/formsExt';
import type { Emits, Group, Props } from './types';
import { genApiAddGroup, genApiGetGroups, genGroupAll, genGroupPlaceholder, genItems, groupNone } from './utils';
import { genFieldFilter } from '@/api/api/index';
import { folderDefault } from '../folders/utils';

const props = withDefaults(defineProps<Props>(), {
	on: undefined,
	autoselect: 'first',
	addChanger: true,
	addIcon: true,
});

const emits = defineEmits<Emits>();

const modelGroupId = defineModel<Props['groupId']>('groupId', { required: true });
const modelGroup = defineModel<Props['group']>('group', { required: true });

const apiGet = props.client && !props.groups ? genApiGetGroups(props.client) : undefined;
const apiAdd = props.client ? genApiAddGroup(props.client) : undefined;

const refSelector = ref<InstanceType<typeof Selector2> | null>(null);

/**
 * Группы выбранной папки
 */
const groupsBySelectedFolder = ref<Group[] | undefined>(undefined);

const filterByFolder = genFieldFilter('folder_id', 'EQUALS', [props.folder?.id ?? 0]);

apiGet?.changeParams({
	project_id: props.projectId,
	folder_id_depth: true,
	filters: [
		filterByFolder,
	],
});

if (props.on !== undefined) {
	apiGet?.params.filters?.push(genFieldFilter('on', 'EQUALS', [Number(props.on)]));
}

/**
 * Синхронная установка группы и id группы
 *
 * Id рекомендуется вместе с группой
 * - для согласованной смены состояния
 * - для уменьшения цепочки `watch` синхронизации состояний `modelGroupId` и `modelGroup`
 *
 * Пример несогласованности: если передать `modelGroup` и значение `modelGroupId`, которое ему не соответсвует, то попытка установить ту же самоую группу в `modelGroup` не приведет к смене `modelGroupId`
 */
const setGroup = (group: Props['group']) => {
	modelGroup.value = group;
	modelGroupId.value = group.id;
};

// Настройка API, обработка смены папки и выбор всех групп
useWatch({
	projectId: () => props.projectId,
	folderId: () => props.folder?.id,
	canSelectAll: () => props.canSelectAll,
}, (changes) => {
	// Настройка API
	if (changes.projectId) {
		apiGet?.changeParams({
			project_id: props.projectId,
		});

		apiGet?.setOptions({
			checkFingerprint: 'TopGroupSelectorGroups:' + props.projectId,
		});
	}

	// Обработка смены папки
	if (changes.folderId) {
		filterByFolder.values = [
			props.folder?.id ?? 0,
		];

		// необходимо установить группы выбранной папки
		if (props.groups) {
			groupsBySelectedFolder.value = genItems(props.groups, props.on, props.folder);
		}
	}

	refSelector.value?.resetCache();

	/**
	 * При смене папки автоматически выбирать нужную группу с учетом `props.autoselect`
	 *
	 * Новая группа, при смене папки не меняется
	 */
	if (
		(changes.folderId?.old !== undefined || changes.canSelectAll?.old !== undefined) &&
		modelGroupId.value !== ITEM_ID_NEW
	) {
		let group: Props['group'] | undefined;

		if (props.autoselect === 'first') {
			group = groupsBySelectedFolder.value?.[0];

			if (props.canSelectAll) {
				group = reactive(genGroupAll());
			}
		}

		if (props.autoselect === 'placeholder' || !group) {
			group = genGroupPlaceholder();
		}

		setGroup(group);
	}
}, { immediate: true });

/**
 * Проверка входного значения `modelGroupId` и изменение `modelGroup`
 *
 * Определяем существует ли группа:
 * - Если существует: меняем группу на указанную
 * - Если не существует: устанавливаем "Нет группы"
 *
 * При загрузке групп по API проверка выполняется в асинхронном режиме
 */
useWatch({
	modelGroup,
	modelGroupId,
}, async (changes) => {
	if (modelGroupId.value === modelGroup.value.id) {
		return;
	}

	// изменение только `modelGroup`
	if (changes.modelGroup && !changes.modelGroupId) {
		modelGroupId.value = modelGroup.value.id;

		return;
	}

	if (props.canAdd && modelGroupId.value === ITEM_ID_NEW) {
		return;
	}

	/**
	 * Для не api
	 */
	if (props.groups) {
		let groupSelected = groupsBySelectedFolder.value?.find((group) => group.id === modelGroupId.value);

		if (!groupSelected && props.canSelectAll) groupSelected = genGroupAll();

		if (groupSelected) {
			setGroup(groupSelected);
		} else {
			const firstGroup = groupsBySelectedFolder.value?.[0];
			if (firstGroup && !modelGroupId.value) {
				// группа не была указана, инициализация компонента
				setGroup(firstGroup);
			} else {
				setGroup(groupNone);
			}
		}
	}

	/**
	 * Для api
	 */
	if (props.client && !props.groups && (modelGroupId.value || modelGroupId.value === ITEM_ID_ALL && !props.canSelectAll)) {
		const api = genApiGetGroups(props.client).changeParams({
			project_id: props.projectId,
			id: modelGroupId.value,
			filters: [
				filterByFolder,
			],
		});

		/**
		 * @remarks При выборе группы "Все группы" или при добавлении группы отмена запроса не происходит
		 */
		api?.setOptions({
			checkFingerprint: 'TopGroupSelectorFindGroup:' + props.projectId,
		});

		const res = await api.call();

		// при отмене запроса, смену группы не выполняем
		if (!res.errors?.length && !res.result) return;

		if (!res.errors && res.result[0]) {
			setGroup(res.result[0] as Group);
		} else {
			setGroup(groupNone);
		}
	}
}, { immediate: true });

const addGroups = async (group: SelectorItem) => {
	// просто выбор произвольной группы, без реального добавления
	if (props.canAdd !== 'api') return;

	// при добавлении группы `props.client` обязателен
	if (!apiAdd) return;

	if (import.meta.env.STORYBOOK) {
		apiAdd.changeParams({
			// @ts-ignore Для storybook
			__sbIsUseAPI: !props.groups,
		});
	}

	const modelGroupOld = modelGroup.value;

	// Для api
	const res = await apiAdd.changeParams({
		project_id: props.projectId,
		names: [group.name],
		to_id: props.folder?.id ?? folderDefault.id,
		to_type: 'in_folder_last',
	}).call();

	if (res.result) {
		setGroup(res.result as unknown as Group);

		emits('addGroup', res.result as unknown as Group);
	} else {
		// вернуть предыдущий выбор в случае ошибки добавления группы
		setGroup(modelGroupOld);
	}

	refSelector.value?.resetCache(true);
};
</script>

<template>
	<Selector2
		ref="refSelector"
		class="top-groupSelector_group"
		v-model="modelGroup"
		:items="groupsBySelectedFolder ?? groups"
		searchType="inline"
		:icon="addIcon ? '' : undefined"
		:api="groups ? undefined : apiGet"
		:apiSetSearchParams="(...args) => apiSetSearchParamsFilter(...args, 'name')"
		:appendSearchToResult="!!canAdd"
		:useAllItem="canSelectAll ? genGroupAll().name : false"
		:addChanger
		useCache
		@appendItem="addGroups"
	>
		<template #item="{ item }">
			<div class="top-groupSelector_groupItem">
				<div
					class="top-comment"
					v-if="item.id === ITEM_ID_NEW"
				>
					{{ $i18n.Common.Add }}:
				</div>

				<span>{{ item.name }}</span>

				<span v-if="item.folder_path && showPath" class="top-groupSelector_groupItemFolderPath">
					{{ item.folder_path }}
				</span>
			</div>
		</template>
	</Selector2>
</template>

<style>
.top-groupSelector_groupItem {
	display: flex;
	flex-direction: column;
	gap: var(--top-padding-1);
}

.top-groupSelector_groupItemFolderPath {
	width: 100%;
	color: var(--color-text-4);
	font-size: 12px;
}
</style>
